import * as Sentry from "@sentry/browser"
import { jwtDecode } from "jwt-decode"
import { marketplaceIsParent, setMarketplaceAuthFlag } from "../util/checkMarketplace"
import { prepareMarketingProperties } from "../util/marketing"
import {
  containsPathInURL,
  getCookie,
  getParameterByName,
  neverThrow,
  parseJsonOrTellSentry,
  removePathInUrl,
  removeURLParameters,
} from "../util/util"
import { AuthenticateSSO, MarketplaceChannel } from "./marketplace"
import { bindPorts, cookieJwtAuth, readTriggersAnalyticsFlagFromJwt, sidebarModeKey } from "./ports"
import type { ElmApp, InactiveFeatureFlags, MostRecentUser } from "./windowElm"
import type { Config } from "@ironzion/frontend-js-config"

const jwtAuthKey = "authentication"
const destination = "initialPath"

declare global {
  interface Window {
    app: ElmApp
  }
}

export const loadMainApp = async (config: Config, container: HTMLElement) => {
  if (window.app) {
    return
  }
  const marketplaceFlag = marketplaceIsParent()
  if (!window.Elm) {
    // If `window.Elm` is not defined by this point, it means the JS file
    // produced by the compiler has failed to load. This happens occasionally.
    // The only thing to do is to reload and hope it works. However, we do so
    // after some amount of jitter, just to make sure that we don't suddenly
    // DDoS ourselves with this configuration in a hypothetical outage where
    // the script is malformed or we point at the wrong asset.
    window.setTimeout(() => window.location.reload(), 2500 + Math.random() * 5000)

    return
  }

  const cookieJwt = getCookie(cookieJwtAuth)
  const localStorageJwt = localStorage.getItem(jwtAuthKey)
  // restore JWT if possible, prefering cookie implementation
  const initialJwt = cookieJwt || localStorageJwt
  // retrieve the exchange token from the url
  // This is present when we first come from Interval
  const exchangeToken = getSessionExchangeToken()

  // restore initialPath if possible
  const savedInitialPath = localStorage.getItem(destination)
  const newInitialPath = getInitialPath(savedInitialPath)
  const viewing = parseJsonOrTellSentry("Viewing preferences", localStorage.getItem("viewing"))
  const viewport = { width: window.innerWidth, height: window.innerHeight }
  const screenSizeWarningDismissed = localStorage.getItem("dismissed-screen-size-warning")
  const dismissedBanners = localStorage.getItem("dismissed-banners")

  await Sentry.startSpan({ name: "starting Elm", op: "ui.elm.init" }, async () => {
    neverThrow(() => {
      setMarketplaceAuthFlag()
    })

    const googleOAuth = await getGoogleOAuthParams()

    window.app = window.Elm.Main.init({
      node: container,
      flags: {
        properties: prepareMarketingProperties(),
        apiUrl: config.apiUrl,
        initialJwt: initialJwt,
        googleOAuth,
        build: config.build_num,
        currentTime: new Date().getTime(),
        // NOTE: The order of auth response consumption matters here, because we remove URL params like "code"
        integrationOAuthResponse: getOAuthResponse(),
        cognitoAuthCodeResponse: await getCognitoAuthCodeResponse(),
        auth0Response: getAuth0Response(),
        vendrAuth0PayloadResponse: getVendrAuth0PayloadResponse(),
        vendrLegacySessionToken: getVendrLegacySessionToken(),
        quickLinkToken: getQuickLinkToken(),
        sessionExchangeToken: exchangeToken,
        buildVersionUrl: config.buildVersionUrl,
        viewing: viewing,
        viewport: viewport,
        screenSizeWarningDismissed: screenSizeWarningDismissed !== null,
        dismissedBanners: Array.from(parseJsonOrTellSentry("dismissed banners", dismissedBanners) || new Set()),

        mostRecentUser: getMostRecentUser(),
        dismissedOnboardingCTA: getDismissedOnboardingCTA(),
        inactiveFeatureFlags: getInactiveFeatureFlags(),
        algoliaAppId: config.algolia.appId,
        algoliaIndex: config.algolia.indexes.default,
        userAgent: navigator.userAgent,
        filestack: {
          apiKey: config.filestack.key,
          signature: config.filestack.signature,
          policy: config.filestack.policy,
        },
        path: newInitialPath,
        userPreferredSidebarMode: getSidebarMode(),
        language: window.navigator.language,
        apiV2Url: config.apiV2Url,
        chromeExtensionId: config.chromeExtensionId,
        workflowBuilderTestModeSeed: +new Date(),
        marketplaceIsParent: marketplaceFlag,
      },
    })
  })

  Sentry.startSpan({ name: "binding ports", op: "app.init.ports" }, () =>
    bindPorts(config, window.app, {
      // This is to prefix all downstream google
      // URLs so we can cleanly tell them apart
      // from WWW paths by prefix-filtering
      basePath: "/app.vendr.com",
      triggersAnalytics: readTriggersAnalyticsFlagFromJwt(initialJwt, false),
      marketplace: Promise.resolve(marketplaceIsParent() ? MarketplaceChannel.connect(config, window.app.ports) : null),
    }),
  )
}

const getSidebarMode = () => localStorage.getItem(sidebarModeKey) || null

const getMostRecentUser = (): MostRecentUser | null => {
  if (getParameterByName("marketplaceLogin")) {
    return null
  }

  const mostRecentUser = localStorage.getItem("most_recent_user")
  return parseJsonOrTellSentry<MostRecentUser>("LocalStorage most_recent_user", mostRecentUser)
}

const getInactiveFeatureFlags = (): InactiveFeatureFlags => {
  const inactiveFeatureFlags = localStorage.getItem("inactive_feature_flags")
  return parseJsonOrTellSentry<InactiveFeatureFlags>("LocalStorage inactive_feature_flags", inactiveFeatureFlags) || []
}

const getDismissedOnboardingCTA = (): boolean | null => {
  const dismissedOnboardingCTA = localStorage.getItem("dismissed_onboarding_cta")
  return parseJsonOrTellSentry("LocalStorage dismissed_onboarding_cta", dismissedOnboardingCTA)
}

/*
|--------------------------------------------------------------------------|
| Parameter helpers                                                        |
|--------------------------------------------------------------------------|
|
| Should the app load with some query parameters, check to see if they are
| of special interest. The params of interest are:
|
| 1. Quickbooks params provided from a Quickbooks redirect.
| 2. Xero params provided from a Xero redirect.
| 3. Some initial path. Used to route users who are coming from a deep link.
|
*/

const getOAuthResponse = () =>
  getQuickbooksOAuthResponse() ||
  getBambooHROAuthResponse() ||
  getMicrosoftGraphOAuthResponse() ||
  getSlackOAuthResponse() ||
  getDocuSignOAuthResponse()

const getGoogleOAuthParams = async (): Promise<{ code: string; referrer: string } | null> => {
  const code = getParameterByName("code")
  const error = getParameterByName("error")

  // BUGBUG: Both Google and BambooHR OAuth cllback query params look similar. They both have a code parameter and scope parameter.
  // BambooHR also has a state parameter. There may be other types of OAuth callback urls that look similar.
  // Google OAuth's scope parameter includes two scopes both of which have the word 'google' in them. We can use this for now
  // to differentiate until we can come up with a better solution
  const isGoogleOAuth = window.location.href.includes("google")
  if (code && isGoogleOAuth) {
    // The state parameter contains the referrer path in the app previous to the
    // google oauth redirect, set there in the authenticationStrategy query.
    const referrer = getParameterByName("state")
    if (!referrer) {
      return null
    }

    /**
     * If the current window has this name to indicate it's part of the marketplace auth flow, and
     * it has a parent, it means we've just completed authenticating in a popup, and rather than
     * consuming the code from google, we want to send the whole redirect url that google sent the
     * user to back to the original iFrame, and then close the popup.
     *
     * Essentially we want google to redirect us to a different window, not just a different url.
     */
    if (window.name === "marketplaceAuth" && self.opener) {
      console.log("IS GOOGLE OAUTH! HANDLE SSO!")
      await AuthenticateSSO.handleSSORedirect({ error })
    }

    return { code, referrer }
  }

  return null
}

const getQuickbooksOAuthResponse = (): { tag: "quickbooks"; authorizationUri: string } | null => {
  const state = getParameterByName("state")

  const expectedAppState = state && localStorage.getItem(state)
  const expectedAppStateJson =
    expectedAppState && parseJsonOrTellSentry<{ authType: string }>("Quickbooks oauth", expectedAppState)
  if (expectedAppStateJson && expectedAppStateJson.authType === "quickbooks") {
    localStorage.removeItem(state)
    return {
      tag: "quickbooks",
      authorizationUri: window.location.href,
    }
  }

  return null
}

const getBambooHROAuthResponse = (): {
  tag: "bamboo"
  urlWithAuthorizationResponseParams: string
  state: unknown
  bambooSubdomain: unknown
} | null => {
  const code = getParameterByName("code")
  const state = getParameterByName("state")
  const bambooHRAppStateJSONString = state && localStorage.getItem(state)
  const bambooHRAppState =
    bambooHRAppStateJSONString &&
    parseJsonOrTellSentry<{ state: unknown; authType: string; subdomain: unknown }>(
      "Bamboo oauth",
      bambooHRAppStateJSONString,
    )

  if (
    code &&
    bambooHRAppState &&
    bambooHRAppState.state &&
    bambooHRAppState.authType === "Bamboo" &&
    bambooHRAppState.subdomain
  ) {
    localStorage.removeItem(state)
    return {
      tag: "bamboo",
      urlWithAuthorizationResponseParams: window.location.href,
      state: bambooHRAppState.state,
      bambooSubdomain: bambooHRAppState.subdomain,
    }
  }

  return null
}

const getSlackOAuthResponse = (): { tag: "slack"; oauthCode: string } | null => {
  const code = getParameterByName("code")
  const state = getParameterByName("state")
  const stringySlackState = state && localStorage.getItem(state)
  const slackState = parseJsonOrTellSentry<{ authType: string }>("Slack oauth", stringySlackState)

  if (code && slackState && slackState.authType === "Slack") {
    if (state) {
      localStorage.removeItem(state)
    }
    return {
      tag: "slack",
      oauthCode: code,
    }
  }

  return null
}

const getDocuSignOAuthResponse = (): { tag: "docusign"; oauthCode: string } | null => {
  const code = getParameterByName("code")
  const state = getParameterByName("state")
  const stringyDocuSignState = state && localStorage.getItem(state)
  const docuSignState = parseJsonOrTellSentry<{ authType: string }>("DocuSign oauth", stringyDocuSignState)

  if (code && docuSignState && docuSignState.authType === "DocuSign") {
    if (state) {
      localStorage.removeItem(state)
    }

    return {
      tag: "docusign",
      oauthCode: code,
    }
  }

  return null
}

const getMicrosoftGraphOAuthResponse = (): { tag: "microsoft"; oauthCode: string } | null => {
  const code = getParameterByName("code")
  const state = getParameterByName("state")
  const stringyState = state && localStorage.getItem(state)
  const integrationState = parseJsonOrTellSentry<{ authType: string }>("Microsoft graph oauth", stringyState)

  if (code && integrationState && integrationState.authType === "MicrosoftGraph") {
    if (state) {
      localStorage.removeItem(state)
    }
    return {
      tag: "microsoft",
      oauthCode: code,
    }
  }

  return null
}

// TODO: all this code feels insanely commonizeable, and could benefit from
// well defined const strings instead of duplicated string
const getCognitoAuthCodeResponse = async (): Promise<{ cognitoCode: string } | null> => {
  // Auth0 callback - using "/auth0" path as a flag to not use Cognito since they both use the "code" query param
  if (containsPathInURL("/callback/auth0")) {
    return null
  }

  const code = getParameterByName("code")
  const state = getParameterByName("state")

  console.log("handle saml redirect, window name:", window.name, "has opener:", !!self.opener)
  if (window.name === "marketplaceAuth" && self.opener) {
    await AuthenticateSSO.handleSSORedirect()
    return null
  }

  const stringySamlSSOAuthState = state && localStorage.getItem(state)
  const samlSSOAuthState = parseJsonOrTellSentry<{ authType: string }>("Cognito oauth", stringySamlSSOAuthState)

  removeURLParameters(["code", "state"])

  if (code && samlSSOAuthState && samlSSOAuthState.authType === "SAML_SSO") {
    if (state) {
      localStorage.removeItem(state)
    }
    localStorage.removeItem("waiting-for-saml-login-code")
    return {
      cognitoCode: code,
    }
  } else if (code && localStorage.getItem("waiting-for-saml-login-code")) {
    localStorage.removeItem("waiting-for-saml-login-code")
    return {
      cognitoCode: code,
    }
  }

  return null
}

const getAuth0Response = (): { auth0Code: string; auth0State: string | null } | null => {
  if (!containsPathInURL("/callback/auth0")) {
    return null
  }

  const code = getParameterByName("code")
  if (!code) {
    return null
  }

  const state = getParameterByName("state")

  removeURLParameters(["code", "state"])
  removePathInUrl("/callback/auth0")

  return {
    auth0Code: code,
    auth0State: state,
  }
}

const getVendrAuth0PayloadResponse = (): { vendrAuthToken: string } | null => {
  const payload = getParameterByName("payload")

  if (payload) {
    try {
      const decoded = jwtDecode<{ state?: string }>(payload)
      if (decoded.state) {
        const stringyAuthState = localStorage.getItem(decoded.state)
        if (stringyAuthState && typeof stringyAuthState === "string") {
          localStorage.removeItem(decoded.state)
          return {
            vendrAuthToken: payload,
          }
        } else {
          throw new Error("Unable to get state from local storage")
        }
      } else {
        throw new Error("Unable to get state from payload")
      }
    } catch (error) {
      console.error(error)
      // Note that `if (Sentry)` will not work here because
      // the code will still crash if Sentry is not defined.
      //
      // if (Sentry) {} --> ReferenceError: Sentry is not defined
      // typeof Sentry !== 'undefined' --> false
      if (typeof Sentry !== "undefined") {
        Sentry.captureException(error)
      } else {
        console.warn("Unable to send error to Sentry")
      }
      return null
    }
  }

  // No payload query param given
  return null
}

const getQuickLinkToken = () => {
  const parameterName = "quickLinkToken"
  const token = getParameterByName(parameterName)

  if (token) {
    removeURLParameters([parameterName])

    return token
  }

  return null
}

const getVendrLegacySessionToken = () => {
  const parameterName = "current_vendr_1_0_session"
  const token = getParameterByName(parameterName)

  if (token) {
    removeURLParameters([parameterName])

    return token
  }

  return null
}

const getSessionExchangeToken = () => {
  const sessionExchangeToken = getParameterByName("sessionExchangeToken")

  if (sessionExchangeToken) {
    removeURLParameters(["sessionExchangeToken"])

    return {
      sessionExchangeToken: sessionExchangeToken,
    }
  }

  return null
}

const getInitialPath = (savedInitialPath: string | null): string => {
  try {
    if (savedInitialPath != null) {
      const savedUrl = new URL(savedInitialPath)
      if (savedUrl.pathname === "/" || savedUrl.pathname === "/login") {
        // let the current path win over a generic '/' or '/login'
        return window.location.href
      }
    }
  } catch (e) {}

  // otherwise, the saved path always wins over the current path
  return savedInitialPath ? savedInitialPath : window.location.href
}
